import React from 'react';
import ReactDOM from 'react-dom';
import { Button, Theme } from '@carbon/react';
import { Close, ChevronLeft, Task as TaskIcon } from '@carbon/icons-react';
import { useTheme } from '../../contexts/ThemeContext';
import './TaskDetailsPanel.css';

// Backend šalje vreme u UTC - prikazujemo ga u CET/CEST (Evropa/Belgrade, Srbija)
const TIME_ZONE = 'Europe/Belgrade';

const STATUS_LABELS = {
  waiting: 'Čeka izvršenje',
  in_progress: 'U toku',
  completed: 'Završen',
  canceled: 'Otkazan',
  cancelled: 'Otkazan',
};

const formatTime = (iso) => {
  if (!iso) return '-';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '-';
  return new Intl.DateTimeFormat('sr-RS', {
    timeZone: TIME_ZONE,
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(d);
};

const formatDate = (iso) => {
  if (!iso) return '-';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '-';
  return new Intl.DateTimeFormat('sr-RS', {
    timeZone: TIME_ZONE,
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).format(d);
};

const severityLabel = (severity) => {
  if (severity === 1) return 'Veoma Važno';
  if (severity === 2) return 'Važno';
  if (severity === 3) return 'Normalno';
  return 'Važno';
};

const severityClass = (severity) => {
  if (severity === 1) return 'high';
  if (severity === 2) return 'medium';
  if (severity === 3) return 'low';
  return 'medium';
};

// Nazivi tipova zaposlenih koji prate zadatak (employeeTypeNames je poravnat
// po indeksu sa employeeTypeIds). Fallback na legacy jedninu.
const getTaskEmployeeTypeNames = (task) => {
  if (!task) return '-';
  if (Array.isArray(task.employeeTypeNames) && task.employeeTypeNames.length > 0) {
    return task.employeeTypeNames.join(', ');
  }
  return task.employeeTypeName || '-';
};

const InfoRow = ({ label, value }) => (
  <div className="adash-detail__info-row">
    <span className="adash-detail__info-label">{label}</span>
    <span className="adash-detail__info-value">{value}</span>
  </div>
);

/**
 * TaskDetailsPanel - desni sidebar sa detaljima izabranog zadatka.
 * Uvek je montiran (radi slide animacije), a `.open` ga uvodi u vidno polje.
 */
const TaskDetailsPanel = ({ task, onClose }) => {
  const { theme } = useTheme();
  const isOpen = Boolean(task);

  return (
    <>
      {ReactDOM.createPortal(
        /* Panel je portal-ovan na document.body (zbog fiksne pozicije ispod
           transform:scale zaglavlja), pa se nalazi van Carbon <Theme> omotača
           iz Shell-a. Zato ga ovde ponovo umotavamo u <Theme> da bi nasledio
           aktivnu temu umesto da padne na svetlu (white). */
        <Theme theme={theme}>
          <div
            className={`adash-detail-panel ${isOpen ? 'open' : ''}`}
            aria-hidden={!isOpen}
          >
          <div className="adash-detail-panel__header">
            <h3 className="adash-detail-panel__title">Detalji zadatka</h3>
            <Button
              kind="ghost"
              size="sm"
              renderIcon={Close}
              iconDescription="Zatvori"
              hasIconOnly
              onClick={onClose}
            />
          </div>

          <div className="adash-detail-panel__content">
            {task && (
              <div className="adash-detail">
                <button type="button" className="adash-detail__back" onClick={onClose}>
                  <ChevronLeft size={16} />
                  Nazad
                </button>

                <div className="adash-detail__header">
                  <div className="adash-detail__icon">
                    <TaskIcon size={28} />
                  </div>
                  <h2 className="adash-detail__title">{task.title}</h2>
                </div>

                <div className="adash-detail__tags">
                  {(task.activityTypeName || task.activityType) && (
                    <span className="adash-chip adash-chip--info">
                      {task.activityTypeName || task.activityType}
                    </span>
                  )}
                  {task.assignToAll && (
                    <span className="adash-chip">Dodeljeno svima</span>
                  )}
                  <span className={`adash-detail__priority adash-detail__priority--${severityClass(task.severity)}`}>
                    {severityLabel(task.severity)}
                  </span>
                  <span className={`adash-chip adash-chip--status adash-chip--${task.status}`}>
                    {STATUS_LABELS[task.status] || task.status}
                  </span>
                </div>

                {task.description && (
                  <div className="adash-detail__section">
                    <h4 className="adash-detail__label">Opis</h4>
                    <p className="adash-detail__value">{task.description}</p>
                  </div>
                )}

                <div className="adash-detail__section">
                  <h4 className="adash-detail__label">Informacije</h4>
                  <div className="adash-detail__info">
                    <InfoRow label="Korisnik" value={task.residentName || '-'} />
                    <InfoRow
                      label="Tip aktivnosti"
                      value={task.activityTypeName || task.activityType || '-'}
                    />
                    <InfoRow
                      label="Zakazano vreme"
                      value={
                        task.scheduledTime
                          ? `${formatDate(task.scheduledTime)} ${formatTime(task.scheduledTime)}`
                          : '-'
                      }
                    />
                    <InfoRow
                      label="Zadužena osoba"
                      value={task.assignToAll ? 'Svi zaposleni' : (task.assignedToName || '-')}
                    />
                    <InfoRow label="Prati" value={getTaskEmployeeTypeNames(task)} />
                    <InfoRow label="Status" value={STATUS_LABELS[task.status] || task.status} />
                    {task.executedAt && (
                      <InfoRow
                        label="Izvršeno"
                        value={`${formatDate(task.executedAt)} ${formatTime(task.executedAt)}`}
                      />
                    )}
                  </div>
                </div>
              </div>
            )}
            </div>
          </div>
        </Theme>,
        document.body
      )}

      {isOpen &&
        ReactDOM.createPortal(
          <div className="adash-detail-panel__overlay" onClick={onClose} />,
          document.body
        )}
    </>
  );
};

export default TaskDetailsPanel;
